import {CreateTaskStatusType, Task, TaskState, TaskPagination, defaultDummyTargetId} from "@/store/modules/task/types";
import { ActionContext, ActionTree, GetterTree, Module, MutationTree } from "vuex";
import { CreateTaskParam, taskAPI, TaskListParam } from "@/api/task";
import { RootState } from "@/store/types";
import { ElMessage } from "element-plus";

export const moduleName = "task"

export enum TaskGetters {
    getTaskList = "GET_TASK_LIST",
    getSelectedProj = "GET_SELECTED_PROJ",
    getSelectedTarget = "GET_SELECTED_TARGET",
    getLoading = "GET_TASK_LOADING",
    getCreateStatus = "GET_CREATE_STATUS",
    getTaskListByUid = "GET_TASK_LIST_BY_UID",
    getTaskByUidLoading = "GET_IS_TASK_BY_UID_LOADING",
    getTaskByUidAllLoaded = "GET_IS_TASK_BY_UID_ALL_LOADED",
    getTaskByUidPagination = "GET_TASK_BY_UID_PAGINATION",
    getIsLoadingByTaskId = "GET_IS_TASK_LOADING_BY_TASK_ID",
    getPayloadByTaskId = "GET_PAYLOAD_BY_TASK_ID",
    getPayloadResByTaskId = "GET_PAYLOAD_RES_BY_TASK_ID",
}

export enum TaskMutations {
    setTaskList = "SET_TASK_LIST",
    setSelectedProj = "SET_SELECTED_PROJ",
    setSelectedTarget = "SET_SELECTED_TARGET",
    setLoading = "SET_TASK_LOADING",
    setCreateStatus = "SET_CREATE_STATUS",
    resetTaskStatus = "RESET_TASK_STATUS",
    setTaskListByUid = "SET_TASK_LIST_BY_UID",
    setTaskByUidLoading = "SET_IS_TASK_BY_UID_LOADING",
    setTaskByUidAllLoaded = "SET_IS_TASK_BY_UID_ALL_LOADED",
    setTaskByUidPagination = "SET_TASK_BY_UID_PAGINATION",
    setIsLoadingByTaskId = "SET_IS_TASK_LOADING_BY_TASK_ID",
    setPayloadByTaskId = "SET_PAYLOAD_BY_TASK_ID",
    resetAll = "RESET_ALL",
}

export enum TaskActions {
    asyncGetOwnTaskListInProjByTypes = "ASYNC_GET_OWN_TASK_LIST_IN_PROJ_BY_TYPES",
    asyncCreateTask = "ASYNC_CREATE_TASK",
    asyncGetTaskListByProjIdAndUidAndTypes = "ASYNC_GET_TASK_LIST_BY_PROJ_ID_AND_UID_AND_TYPES",
    asyncGetPayloadByTaskId = "ASYNC_GET_PAYLOAD_BY_TASK_ID",
}

const defaultPagination: TaskPagination = {
    pageNum: 1,
    pageSize: 10,
}

const defaultState: TaskState = {
    taskList: [],
    selectedProj: "",
    dummyTargetId: defaultDummyTargetId,
    selectedTarget: "",
    isLoading: false,
    createStatus: "idle",
    taskListByUid: [],
    isTaskByUidLoading: false,
    isTaskByUidAllLoaded: false,
    taskPagination: defaultPagination,
    taskListMap: {},
}

const state: TaskState = { ...defaultState }

export const getters: GetterTree<TaskState, RootState> = {
    [TaskGetters.getTaskList](state): Task[] {
        return state.taskList;
    },
    [TaskGetters.getSelectedProj](state): string {
        return state.selectedProj;
    },
    [TaskGetters.getSelectedTarget](state): string {
        return state.selectedTarget;
    },
    [TaskGetters.getLoading](state): boolean {
        return state.isLoading;
    },
    [TaskGetters.getCreateStatus](state): string {
        return state.createStatus;
    },
    [TaskGetters.getTaskListByUid](state): Task[] {
        return state.taskListByUid;
    },
    [TaskGetters.getTaskByUidLoading](state): boolean {
        return state.isTaskByUidLoading;
    },
    [TaskGetters.getTaskByUidAllLoaded](state): boolean {
        return state.isTaskByUidAllLoaded;
    },
    [TaskGetters.getTaskByUidPagination](state): TaskPagination {
        return state.taskPagination;
    },
    [TaskGetters.getPayloadByTaskId]: (state) => (taskId: string): string => {
        if (state.taskListMap[taskId]) {
            return state.taskListMap[taskId].payload
        } else {
            return ""
        }
    },
    [TaskGetters.getPayloadResByTaskId]: (state) => (taskId: string): string => {
        if (state.taskListMap[taskId]) {
            return state.taskListMap[taskId].payloadRes
        } else {
            return ""
        }
    },
    [TaskGetters.getIsLoadingByTaskId]: (state) => (taskId: string): boolean => {
        if (state.taskListMap[taskId]) {
            return state.taskListMap[taskId].isLoading
        } else {
            return false
        }
    },
}

export const mutations: MutationTree<TaskState> = {
    [TaskMutations.setTaskList](state, payload: Task[]): void {
        state.taskList = payload === null ? [] : payload;
    },
    [TaskMutations.setSelectedProj](state, payload: string): void {
        state.selectedProj = payload;
    },
    [TaskMutations.setSelectedTarget](state, payload: string): void {
        state.selectedTarget = payload;
    },
    [TaskMutations.setLoading](state, payload: boolean): void {
        state.isLoading = payload;
    },
    [TaskMutations.setCreateStatus](state, payload: CreateTaskStatusType): void {
        state.createStatus = payload;
    },
    [TaskMutations.resetTaskStatus](state): void {
        Object.assign(state, { ...defaultState, taskList: [] });
    },
    [TaskMutations.setTaskListByUid](state, payload: Task[]): void {
        state.taskListByUid = state.taskListByUid.concat(payload);
    },
    [TaskMutations.setTaskByUidLoading](state, payload: boolean): void {
        state.isTaskByUidLoading = payload;
    },
    [TaskMutations.setTaskByUidAllLoaded](state, payload: boolean): void {
        state.isTaskByUidAllLoaded = payload;
    },
    [TaskMutations.setTaskByUidPagination](state, payload: TaskPagination): void {
        state.taskPagination = payload;
    },
    [TaskMutations.setPayloadByTaskId](state, payload: { taskId: string, taskInfo: Task }): void {
        state.taskListMap[payload.taskId].payload = payload.taskInfo.payload
        state.taskListMap[payload.taskId].payloadRes = payload.taskInfo.resPayload
    },
    [TaskMutations.setIsLoadingByTaskId](state, payload: { taskId: string, isLoading: boolean }): void {
        if (state.taskListMap[payload.taskId]) {
            state.taskListMap[payload.taskId].isLoading = payload.isLoading
        } else {
            state.taskListMap[payload.taskId] = {
                payload: "",
                payloadRes: "",
                isLoading: payload.isLoading,
            }
        }
    },
    [TaskMutations.resetAll](state) {
        Object.assign(state, {
            ...defaultState,
            taskList: [],
            taskListByUid: [],
            taskListMap: {},

        })
    },

}

export const actions: ActionTree<TaskState, RootState> = {
    async [TaskActions.asyncGetOwnTaskListInProjByTypes](
        { commit }: ActionContext<TaskState, RootState>,
        taskListParam: TaskListParam,
    ): Promise<void> {
        try {
            commit(TaskMutations.setLoading, true);
            const taskList = await taskAPI.getOwnTaskListInProjByTypes(taskListParam);
            commit(TaskMutations.setTaskList, taskList)
        } catch (err) {
            console.log(err);
        } finally {
            commit(TaskMutations.setLoading, false);
        }
    },
    async [TaskActions.asyncCreateTask](
        { commit }: ActionContext<TaskState, RootState>,
        createTaskParam: CreateTaskParam,
    ) {
        try {
            commit(TaskMutations.setLoading, true);
            commit(TaskMutations.setCreateStatus, "processing");
            await taskAPI.createTask(createTaskParam);
            ElMessage.success("生成任务已创建，运行中。");
            commit(TaskMutations.setCreateStatus, "success");

        } catch (err) {
            ElMessage.error("生成任务创建失败。");
            commit(TaskMutations.setCreateStatus, "failed");
        } finally {
            commit(TaskMutations.setLoading, false);
        }
    },
    async [TaskActions.asyncGetTaskListByProjIdAndUidAndTypes](
        { commit }: ActionContext<TaskState, RootState>,
        { projId, uid, typeList }: { projId: string, uid: string, typeList: string },
    ): Promise<void> {
        try {
            commit(TaskMutations.setTaskByUidLoading, true);
            const taskList = await taskAPI.GetTaskListByProjIdAndTargetIdAndTypes(
                { projId, uid, typeList }
            );
            commit(TaskMutations.setTaskListByUid, taskList);
        } catch (err) {
            console.log(err);
        } finally {
            commit(TaskMutations.setTaskByUidLoading, false);
        }
    },
    async [TaskActions.asyncGetPayloadByTaskId](
        { commit }: ActionContext<TaskState, RootState>,
        { projId, uid, taskId }: { projId: string, uid: string, taskId: string },
    ): Promise<void> {
        try {
            commit(TaskMutations.setIsLoadingByTaskId, { taskId: taskId, isLoading: true });
            const taskInfo = await taskAPI.GetPayloadByTaskId({ projId, uid, taskId });
            commit(TaskMutations.setPayloadByTaskId, { taskId: taskId, taskInfo: taskInfo });
        } catch (err) {
            console.log(err);
        } finally {
            commit(TaskMutations.setIsLoadingByTaskId, { taskId: taskId, isLoading: false });
        }
    },
}

export const task: Module<TaskState, RootState> = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
}